import React from "react";
import { Spinner } from "@/assets/svg/Spinner";
import { CloudUpload, HeartHandshake } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui";
import { i18n } from "@/i18n";

interface CsvFileInputProps {
  disabled: boolean;
  onUpload: (file?: File) => void;
  loading: boolean;
}

export function CsvFileInput({
  disabled,
  onUpload,
  loading,
}: CsvFileInputProps) {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.basicInformation.csvImport",
  });

  if (loading) {
    return (
      <section className="flex h-60 flex-col items-center justify-center gap-6 rounded bg-gray-50 p-8 shadow-sm dark:bg-input">
        <Spinner className="size-4 fill-white text-black/50" />
        <p className="text-sm font-medium text-gray-700">{t("uploading")}</p>
      </section>
    );
  } else {
    return <CsvInput disabled={disabled} onUpload={onUpload} />;
  }
}

interface CsvInputProps {
  disabled: boolean;
  onUpload: (file?: File) => void;
}

function CsvInput({ disabled, onUpload }: CsvInputProps) {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.basicInformation.csvImport",
  });

  return (
    <section
      className="flex flex-col items-center gap-6 rounded bg-gray-50 p-8 shadow-sm"
      onDrop={(event) => {
        event.preventDefault();
        if (!disabled) {
          onUpload(event.dataTransfer.files[0]);
        }
      }}
      onDragOver={(event) => event.preventDefault()}
    >
      <CloudUpload className="size-12 text-icon" />
      <div className="flex flex-col items-center gap-1">
        <strong className="text-sm leading-5 font-medium text-foreground-medium">
          {t("dragAndDrop")}
        </strong>
        <label
          htmlFor="inputFileUpload"
          className="cursor-pointer text-xs font-medium text-blue-primary underline"
        >
          {t("selectAFile")}
        </label>
      </div>
      <input
        id="inputFileUpload"
        data-testid="input-file-upload"
        type="file"
        accept=".csv"
        disabled={disabled}
        onChange={({ target: { files } }) =>
          onUpload(files?.item(0) || undefined)
        }
        hidden
      />
      <InstructionDialog />
    </section>
  );
}

function InstructionDialog() {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.basicInformation.csvImport",
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <HeartHandshake className="h-4" />
          {t("seeInstructions")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("instructionsHeader")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 text-sm text-primary">
          <p>{t("instructions")}</p>
          <a
            className="underline"
            href="https://docs.google.com/spreadsheets/d/1jdh-oVQTAiUMuGBGSkfI4gOfaibE673MP09AMVUmp4Y/edit?usp=sharing"
            target="_blank"
            rel="noreferrer"
          >
            {t("exampleFile")}
          </a>
          <p>
            <Trans i18nKey="bulkMessaging.basicInformation.csvImport.contact">
              Contact your Director of Partner Success or email
              <a className="text-blue-primary" href="mailto:support@zeemee.com">
                support@zeemee.com
              </a>
              for additional help.
            </Trans>
          </p>
        </div>
        <DialogFooter className="sm:justify-end">
          <DialogClose asChild>
            <Button type="button">{i18n.t("global.close")}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
